<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <h4 class="text-capitalize fw-500 breadcrumb-title">
                            <i class="fas fa-truck"></i> Statistik Hit Ongkir (RajaOngkir)
                        </h4>
                        <div class="action-btn d-flex align-items-center flex-wrap" style="gap:8px;">
                            <label class="mb-0 small text-muted mr-1">Tanggal (WIB):</label>
                            <input
                                v-model="filterDate"
                                type="date"
                                class="form-control form-control-sm"
                                style="width:auto;"
                                @change="loadAll"
                            />
                            <button type="button" class="btn btn-sm btn-outline-secondary" @click="setToday">
                                Hari ini
                            </button>
                            <button @click="loadAll" class="btn btn-light">
                                <i class="fas fa-sync"></i> Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="isTodayFilter && summary.quota_used_pct >= 80" class="alert alert-warning">
                Kuota API RajaOngkir hari ini sudah <b>{{ summary.quota_used_pct }}%</b>
                ({{ formatInt(summary.hits_today) }} / {{ formatInt(summary.daily_limit) }} hit).
            </div>

            <div class="row mb-3">
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Hit {{ dayLabel }}</small>
                        <h3 class="mb-0 text-primary">{{ formatInt(summary.hits_today) }}</h3>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Hit Bulan {{ monthLabel }}</small>
                        <h3 class="mb-0 text-success">{{ formatInt(summary.hits_month) }}</h3>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Cari Lokasi {{ dayLabelShort }}</small>
                        <h3 class="mb-0 text-info">{{ formatInt(summary.search_today) }}</h3>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Hitung Tarif {{ dayLabelShort }}</small>
                        <h3 class="mb-0 text-warning">{{ formatInt(summary.calculate_today) }}</h3>
                    </div>
                </div>
            </div>

            <div class="row mb-3">
                <div class="col-md-4">
                    <div class="card p-3">
                        <h6 class="mb-3">Per Action ({{ dayLabelShort }})</h6>
                        <div v-if="byAction.length === 0" class="text-muted small">Belum ada data</div>
                        <div v-for="row in byAction" :key="row.action" class="d-flex justify-content-between border-bottom py-1">
                            <span>{{ labelAction(row.action) }}</span>
                            <b>{{ formatInt(row.count) }}</b>
                        </div>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="card p-3">
                        <h6 class="mb-3">Per Sumber ({{ dayLabelShort }})</h6>
                        <div v-if="bySource.length === 0" class="text-muted small">Belum ada data</div>
                        <div v-for="row in bySource" :key="row.source" class="d-flex justify-content-between border-bottom py-1">
                            <span>{{ labelSource(row.source) }}</span>
                            <b>{{ formatInt(row.count) }}</b>
                        </div>
                    </div>
                </div>
                <div class="col-md-4">
                    <div class="card p-3">
                        <h6 class="mb-3">Ringkasan ({{ dayLabelShort }})</h6>
                        <div class="d-flex justify-content-between py-1"><span>Gagal</span><b>{{ formatInt(summary.failed_today) }}</b></div>
                        <div class="d-flex justify-content-between py-1"><span>User aktif</span><b>{{ formatInt(summary.active_users_today) }}</b></div>
                        <div class="d-flex justify-content-between py-1"><span>Lifetime hit</span><b>{{ formatInt(summary.hits_lifetime) }}</b></div>
                        <div class="d-flex justify-content-between py-1"><span>Limit harian (env)</span><b>{{ formatInt(summary.daily_limit) }}</b></div>
                    </div>
                </div>
            </div>

            <div class="card p-3 mb-3">
                <h6 class="mb-1">Hit per Hari (14 hari terakhir, WIB)</h6>
                <small class="text-muted d-block mb-3">Klik baris untuk filter tanggal</small>
                <div v-if="daily.length === 0" class="text-muted">Belum ada data</div>
                <table v-else class="table table-sm mb-0">
                    <thead><tr><th>Tanggal</th><th class="text-right">Hit</th></tr></thead>
                    <tbody>
                        <tr
                            v-for="d in daily"
                            :key="d.date"
                            class="daily-row"
                            :class="{ 'table-active': d.date === filterDate }"
                            @click="pickDate(d.date)"
                        >
                            <td>{{ d.date }}</td>
                            <td class="text-right"><b>{{ formatInt(d.count) }}</b></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="card p-0" style="overflow-x:auto;">
                <div class="p-3 border-bottom">
                    <h6 class="mb-0">Top User — {{ filterDate }} (WIB)</h6>
                </div>
                <div v-if="loading" class="text-center p-4"><div class="spinner-border"></div></div>
                <table v-else class="table table-hover mb-0" style="font-size:13px;">
                    <thead style="background:#f9fafb;">
                        <tr>
                            <th>User</th>
                            <th class="text-right">Hit Hari Terpilih</th>
                            <th class="text-right">Hit Bulan Ini</th>
                            <th>Terakhir Hit (hari terpilih)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="users.length === 0">
                            <td colspan="4" class="text-center text-muted p-4">
                                Belum ada hit ongkir pada {{ filterDate }}
                            </td>
                        </tr>
                        <tr v-for="u in users" :key="u.user_id">
                            <td>
                                <div><b>{{ u.user_email || ('User #' + u.user_id) }}</b></div>
                                <small class="text-muted">{{ u.user_phone || '-' }}</small>
                            </td>
                            <td class="text-right">{{ formatInt(u.hits_today) }}</td>
                            <td class="text-right">{{ formatInt(u.hits_month) }}</td>
                            <td>{{ formatTime(u.last_hit_at) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

function wibTodayISO() {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Jakarta',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(new Date());
    const y = parts.find((p) => p.type === 'year').value;
    const m = parts.find((p) => p.type === 'month').value;
    const d = parts.find((p) => p.type === 'day').value;
    return `${y}-${m}-${d}`;
}

export default {
    data() {
        return {
            loading: false,
            filterDate: wibTodayISO(),
            summary: {
                hits_today: 0,
                hits_month: 0,
                hits_lifetime: 0,
                search_today: 0,
                calculate_today: 0,
                failed_today: 0,
                active_users_today: 0,
                daily_limit: 100,
                quota_used_pct: 0,
            },
            byAction: [],
            bySource: [],
            daily: [],
            users: [],
        };
    },
    computed: {
        isTodayFilter() {
            return this.filterDate === wibTodayISO();
        },
        dayLabel() {
            return this.isTodayFilter ? 'Hari Ini (WIB)' : this.filterDate;
        },
        dayLabelShort() {
            return this.isTodayFilter ? 'Hari Ini' : this.filterDate;
        },
        monthLabel() {
            if (!this.filterDate) return '';
            const [y, m] = this.filterDate.split('-');
            return `${m}/${y}`;
        },
    },
    mounted() {
        this.loadAll();
    },
    methods: {
        setToday() {
            this.filterDate = wibTodayISO();
            this.loadAll();
        },
        pickDate(dateStr) {
            if (!dateStr || dateStr === this.filterDate) return;
            this.filterDate = dateStr;
            this.loadAll();
        },
        async loadAll() {
            this.loading = true;
            const dateParam = { date: this.filterDate };
            try {
                const [summaryRes, usersRes] = await Promise.all([
                    axios.get('admin/ongkir-stats/summary', { params: dateParam }),
                    axios.get('admin/ongkir-stats/users', { params: { ...dateParam, limit: 25 } }),
                ]);
                const data = summaryRes.data.data || {};
                this.summary = data.summary || this.summary;
                this.byAction = data.by_action || [];
                this.bySource = data.by_source || [];
                this.daily = data.daily || [];
                if (data.selected_date) {
                    this.filterDate = data.selected_date;
                }
                const userData = usersRes.data.data || {};
                this.users = userData.items || [];
            } catch (e) {
                console.error(e);
                this.$toast && this.$toast.error('Gagal memuat statistik ongkir');
            } finally {
                this.loading = false;
            }
        },
        formatInt(n) {
            return Number(n || 0).toLocaleString('id-ID');
        },
        formatTime(ms) {
            if (!ms) return '-';
            return new Date(Number(ms)).toLocaleString('id-ID', { timeZone: 'Asia/Jakarta' });
        },
        labelAction(action) {
            if (action === 'search_destination') return 'Cari lokasi';
            if (action === 'calculate_cost') return 'Hitung tarif';
            return action;
        },
        labelSource(source) {
            const map = {
                bot_ai: 'Bot AI (chat)',
                validate_save: 'Validasi simpan ongkir',
                admin_search: 'Pencarian lokasi (UI)',
                cs_panel: 'Customer Service',
                shop: 'Shop',
                system: 'System',
            };
            return map[source] || source;
        },
    },
};
</script>

<style scoped>
.daily-row {
    cursor: pointer;
}
.daily-row:hover {
    background-color: #f3f4f6;
}
</style>
